/**
 * Five stars, filled to a rating — `4.3` fills four and a third.
 *
 * <p>Decorative: the stars are hidden from assistive technology and the
 * `label` says the rating in words instead, because "star star star star
 * star" read aloud tells nobody anything.
 *
 * <p>No hooks and no `'use client'`, so the product page's server render and
 * the reviews section's client render draw the same markup.
 */

const STAR_PATH =
  'M10 1.6l2.6 5.28 5.82.85-4.21 4.1.99 5.8L10 14.9l-5.2 2.73.99-5.8-4.21-4.1 5.82-.85L10 1.6z';

export function StarIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" className={className}>
      <path d={STAR_PATH} fill="currentColor" />
    </svg>
  );
}

export function ReviewStars({
  rating,
  label,
  className = 'h-4 w-4',
}: {
  rating: number;
  label: string;
  /** Size of one star. */
  className?: string;
}) {
  const filled = `${Math.max(0, Math.min(5, rating)) * 20}%`;
  const row = (tone: string) => (
    <span className={`flex ${tone}`}>
      {[0, 1, 2, 3, 4].map((index) => (
        <StarIcon key={index} className={`${className} shrink-0`} />
      ))}
    </span>
  );
  return (
    <span className="relative inline-flex">
      <span className="sr-only">{label}</span>
      {row('text-[var(--color-neutral-200,#e5e7eb)]')}
      <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: filled }}>
        {row('text-amber-500')}
      </span>
    </span>
  );
}
